import React, { useEffect, useMemo } from "react";
import { Row, Col, Select, Input, Switch, Upload, Button, InputNumber } from "antd";
import { Controller, useWatch } from "react-hook-form";
import { UploadOutlined } from "@ant-design/icons";
import Error from "./Error";
import MDTypography from "components/MDTypography";
import UploadField from "../UploadField";
import { numericRules } from "../Schema";

const PropertyStep = ({
  control,
  errors,
  propertyList,
  setValue,
  propertyConfig,
  watch,
  ocfDisplayId,
  ocf,
}) => {
  const propertyId = useWatch({
    control,
    name: "property.name",
  });

  const currentType = useWatch({
    control,
    name: "property.type",
  });

  const isDiscount = useWatch({
    control,
    name: "property.isDiscount",
  });

  // const commissionType = useWatch({
  //   control,
  //   name: "property?.commissionType",
  // });

  const selectedProperty = useMemo(() => {
    return propertyList?.find((p) => String(p.name) === String(propertyId));
  }, [propertyId, propertyList]);
  const commissionType = selectedProperty?.commissionType;
  const mappedTypeSizes = selectedProperty?.typeSize || [];

  const revenueLabel = (commissionType, type) => {
    if (!commissionType) return type;
    if (commissionType === "percentage") {
      return `${type} (%)`;
    }
    if (commissionType === "fixed") {
      return `${type} (Fixed  per ${selectedProperty?.sizeUnit})`;
    }
    return `${type} (${commissionType})`;
  };

  const gstOptions = useMemo(() => {
    const map = {};
    propertyConfig?.gst?.forEach((item) => {
      map[item.key] = item.value;
    });
    return map;
  }, [propertyConfig]);

  useEffect(() => {
    if (gstOptions?.otherCharges) {
      if (ocf?.propertyResponse) {
        setValue("property.otherChargesGst", ocf?.propertyResponse?.otherChargesGst);
      } else {
        setValue("property.otherChargesGst", gstOptions.otherCharges);
      }
    }
    if (gstOptions?.bsp) {
      if (ocf?.propertyResponse) {
        setValue("property.bspGst", ocf?.propertyResponse?.bspGst);
      } else {
        setValue("property.bspGst", gstOptions.bsp);
      }
    }
    setValue("property.commissionType", selectedProperty?.commissionType);
    setValue("property.commission", selectedProperty?.commission, { shouldValidate: true });
    setValue("property.commissionDiscountUnit", selectedProperty?.commissionType);
    setValue("property.sizeUnit", selectedProperty?.sizeUnit);
    setValue("property.billingPercentage", selectedProperty?.billingPercentage);
  }, [gstOptions, setValue, selectedProperty, ocf]);

  const colProps = { xs: 24, sm: 12, md: 12, lg: 8 };

  return (
    <Row gutter={[16, 16]} style={{ margin: "28px 0" }}>
      <Col span={8} {...colProps}>
        <label>Property</label>
        <Controller
          name="property.name"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              style={{ width: "100%" }}
              placeholder="Select Property"
              status={errors?.property?.name && "error"}
              onChange={(value) => {
                field.onChange(value);
                const property = propertyList.find((p) => p.name === value);
                if (property) {
                  setValue("property.bsp", property.bsp); // 👈 nested path
                  setValue("property.revenuePercentage", property?.commission);
                  setValue("property?.commissionType", property?.commissionType);
                  setValue("property.type", null);
                  setValue("property.size", null);
                  setValue("property.paymentPlan", null);
                }
              }}
            >
              {propertyList?.map((item) => (
                <Select.Option key={item.id} value={item.name}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          )}
        />

        <Error name="property.name" />
      </Col>

      {/* ================= TYPE ================= */}
      <Col span={8} {...colProps}>
        <label>Type</label>

        <Controller
          name="property.type"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              style={{ width: "100%" }}
              onChange={(value) => {
                field.onChange(value);
                setValue("property.size", null);
              }}
            >
              {mappedTypeSizes.map((item) => (
                <Select.Option key={item.type} value={item.type}>
                  {item.type}
                </Select.Option>
              ))}
            </Select>
          )}
        />
        <Error name="property.type" />
      </Col>

      {/* ================= SIZE ================= */}
      <Col span={8} {...colProps}>
        <label>Size</label>

        <Controller
          name="property.size"
          control={control}
          render={({ field }) => (
            <Select {...field} style={{ width: "100%" }}>
              {mappedTypeSizes
                ?.find((i) => i.type === currentType)
                ?.size?.map((s) => (
                  <Select.Option key={s} value={s}>
                    {s}
                  </Select.Option>
                ))}
            </Select>
          )}
        />
        <Error name="property.size" />
      </Col>

      {/* ================= PAYMENT PLAN ================= */}
      <Col span={8} {...colProps}>
        <label>Payment Plan</label>

        <Controller
          name="property.paymentPlan"
          control={control}
          render={({ field }) => (
            <Select {...field} style={{ width: "100%" }}>
              {selectedProperty?.paymentPlan?.map((p) => (
                <Select.Option key={p} value={p}>
                  {p}
                </Select.Option>
              ))}
            </Select>
          )}
        />
        <Error name="property.paymentPlan" />
      </Col>

      {/* ================= BSP ================= */}
      <Col span={8} {...colProps}>
        <label>BSP {selectedProperty?.sizeUnit && `(Per ${selectedProperty?.sizeUnit})`}</label>

        <Controller
          name="property.bsp"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, "");
                field.onChange(value);
              }}
            />
          )}
        />

        <Error name="property.bsp" />
      </Col>

      <Col span={8} {...colProps}>
        <label>BSP GST %</label>
        <Controller
          name="property.bspGst"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, "");
                field.onChange(value);
              }}
              status={errors?.property?.bsp && "error"}
            />
          )}
        />
        <Error name="property.bspGst" />
      </Col>

      <Col span={8} {...colProps}>
        <label>
          Other Charges {selectedProperty?.sizeUnit && `(Per ${selectedProperty?.sizeUnit})`}
        </label>
        <Controller
          name="property.otherCharges"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              status={errors?.property?.bsp && "error"}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, "");
                field.onChange(value);
              }}
            />
          )}
        />
        <Error name="property.otherCharges" />
      </Col>

      <Col span={8} {...colProps}>
        <label>Other Charges GST %</label>
        <Controller
          name="property.otherChargesGst"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              status={errors?.property?.otherChargesGst && "error"}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, "");
                field.onChange(value);
              }}
            />
          )}
        />
        <Error name="property.otherChargesGst" />
      </Col>

      {/* ================= DISCOUNT SWITCH ================= */}

      <Col span={8} {...colProps}>
        <label>PLC {selectedProperty?.sizeUnit && `(Per ${selectedProperty?.sizeUnit})`}</label>
        <Controller
          name="property.plc"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              status={errors?.property?.plc && "error"}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, "");
                field.onChange(value);
              }}
            />
          )}
        />
        <Error name="property.plc" />
      </Col>

      <Col span={8} {...colProps}>
        <label>{revenueLabel(commissionType, "Revenue")}</label>
        <Controller
          name="property.commission"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              status={errors?.property?.commission && "error"}
              style={{
                WebkitTextFillColor: "rgba(0, 0, 0, 0.85)", // Required for some browsers (Safari/Chrome)
                color: "rgba(0, 0, 0, 0.85)",
              }}
            />
          )}
          disabled
        />
        <Error name="property.commission" />
      </Col>

      <Col span={8} {...colProps}>
        <label>Any Discount?</label>

        <Controller
          name="property.isDiscount"
          control={control}
          render={({ field }) => (
            <Switch checked={!!field.value} onChange={(checked) => field.onChange(checked)} />
          )}
        />

        <Error name="property.isDiscount" />
      </Col>
      {/* ================= CONDITIONAL DISCOUNT ================= */}
      {isDiscount && (
        <>
          <Col span={8} {...colProps}>
            <label>{revenueLabel(commissionType, "Revenue Discount")}</label>
            <Controller
              name="property.discount"
              control={control}
              render={({ field }) => (
                // <Input
                //   {...field}
                //   onChange={(e) => {
                //     const value = Number(e.target.value) || 0;
                //     const max = Number(selectedProperty?.commission) || 0;

                //     // 🚀 clamp to commission
                //     field.onChange(value > max ? max : value);
                //   }}
                // />

                <Input
                  {...field}
                  inputMode="decimal"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d{0,5}$/.test(value)) {
                      field.onChange(value);
                    }
                  }}
                />
              )}
            />
            <Error name="property.discount" />
          </Col>

          <Col span={8} {...colProps}>
            <div className="form-field">
              <label className="form-label">Discount Approval File</label>

              <UploadField
                name="property.dis_approval_file"
                fieldType="dis_approval_file"
                label="Copy of Discount Approval"
                ocfDisplayId={ocfDisplayId}
              />
            </div>
            <Error name="property.dis_approval_file" />
          </Col>
        </>
      )}
      <Col span={24}>
        <MDTypography variant="h6">Inventory Details </MDTypography>
      </Col>
      <Col span={8} {...colProps}>
        <label>Unit No</label>
        <Controller
          name="property.unitNo"
          control={control}
          render={({ field }) => <Input {...field} status={errors?.property?.unitNo && "error"} />}
        />
        <Error name="property.unitNo" />
      </Col>

      <Col span={8} {...colProps}>
        <label>Floor No</label>
        <Controller
          name="property.floorNo"
          control={control}
          render={({ field }) => <Input {...field} status={errors?.property?.floorNo && "error"} />}
        />
        <Error name="property.floorNo" />
      </Col>

      <Col span={8} {...colProps}>
        <label>Tower/Block No</label>
        <Controller
          name="property.towerNo"
          control={control}
          render={({ field }) => <Input {...field} status={errors?.property?.towerNo && "error"} />}
        />
        <Error name="property.towerNo" />
      </Col>
    </Row>
  );
};

export default PropertyStep;
